import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

const colorMap = {
  blue: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400', icon: 'text-blue-600 dark:text-blue-400' },
  green: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-600 dark:text-emerald-400', icon: 'text-emerald-600 dark:text-emerald-400' },
  red: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-600 dark:text-red-400', icon: 'text-red-600 dark:text-red-400' },
  yellow: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-600 dark:text-amber-400', icon: 'text-amber-600 dark:text-amber-400' },
  purple: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-600 dark:text-purple-400', icon: 'text-purple-600 dark:text-purple-400' },
  cyan: { bg: 'bg-cyan-100 dark:bg-cyan-900/30', text: 'text-cyan-600 dark:text-cyan-400', icon: 'text-cyan-600 dark:text-cyan-400' },
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: {
    opacity: 1, y: 0, scale: 1,
    transition: { type: 'spring', damping: 20, stiffness: 200 },
  },
  hover: {
    y: -4,
    boxShadow: '0 12px 30px rgba(0,0,0,0.08)',
    transition: { duration: 0.2 },
  },
};

function AnimatedCounter({ value, duration = 1000 }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime = null;
    const startVal = 0;
    const endVal = typeof value === 'number' ? value : parseFloat(value) || 0;
    const frame = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(startVal + (endVal - startVal) * eased));
      if (progress < 1) requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  }, [value, duration]);

  return <span>{count.toLocaleString()}</span>;
}

export default function KpiGrid({ cards = [], columns = 4 }) {
  const gridCols = {
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-2 md:grid-cols-4',
    6: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6',
  };

  if (cards.length === 0) return null;

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className={`grid gap-4 ${gridCols[columns] || 'grid-cols-2 md:grid-cols-4'}`}
    >
      {cards.map((card, idx) => {
        const colors = colorMap[card.color] || colorMap.blue;
        const isUp = card.trend && card.trend > 0;
        const trendColor = isUp
          ? 'text-emerald-600 dark:text-emerald-400'
          : card.trend < 0
          ? 'text-red-600 dark:text-red-400'
          : 'text-gray-400 dark:text-gray-500';

        return (
          <motion.div
            key={card.key || idx}
            variants={cardVariants}
            whileHover="hover"
            onClick={card.onClick}
            className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 md:p-5 shadow-sm ${
              card.onClick ? 'cursor-pointer' : ''
            }`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`flex items-center justify-center w-10 h-10 rounded-xl ${colors.bg}`}>
                {card.icon && <card.icon size={20} className={colors.icon} />}
              </div>
              {card.trend !== undefined && card.trend !== null && (
                <div className={`flex items-center gap-0.5 text-xs font-medium ${trendColor}`}>
                  {card.trend > 0 ? (
                    <TrendingUp size={12} />
                  ) : card.trend < 0 ? (
                    <TrendingDown size={12} />
                  ) : (
                    <Minus size={12} />
                  )}
                  <span>{Math.abs(card.trend).toFixed(1)}%</span>
                </div>
              )}
            </div>
            <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mb-1 truncate">
              {card.label}
            </p>
            <p className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white truncate">
              {card.prefix || ''}
              <AnimatedCounter value={card.value} />
              {card.suffix || ''}
            </p>
            {card.subtext && (
              <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500 truncate">
                {card.subtext}
              </p>
            )}
          </motion.div>
        );
      })}
    </motion.div>
  );
}
